import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { buscarIW, fmtExportacao } from './iw/apiIW';

const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#475569', primaryDark: '#3730a3' };
const GERMES = [
  { id: 'mrsa', nome: 'MRSA' }, { id: 'erc', nome: 'ERC / ESBL' }, { id: 'crpa', nome: 'CRPA' },
  { id: 'vre', nome: 'VRE' }, { id: 'crab', nome: 'CRAB (KPC)' }, { id: 'mmr', nome: 'MMR' }, { id: 'mmro', nome: 'MMR outros' },
];
const PRECAUCOES = [{ id: 'contato', nome: 'Contato' }, { id: 'aerea', nome: 'Aérea' }, { id: 'respiratoria', nome: 'Respiratória' }];
const UNIDADES = ['Todas', 'VitalCare', 'HomeBaby'];
// Números pequenos nos gráficos aparecem como "<5" para não expor pacientes.
const fmtPequeno = n => (n > 0 && n < 5 ? '<5' : String(n));

function Kpi({ titulo, valor, detalhe, alerta }) {
  return (
    <div style={{ background: alerta ? '#fff7ed' : P.white, border: `1px solid ${alerta ? '#fed7aa' : P.border}`, borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: alerta ? '#9a3412' : P.muted }}>{titulo}</span>
      <span style={{ fontSize: '1.8rem', fontWeight: 800, color: alerta ? '#9a3412' : P.text }}>{valor}</span>
      <span style={{ fontSize: '0.74rem', color: alerta ? '#7c2d12' : '#64748b' }}>{detalhe}</span>
    </div>
  );
}

export default function PainelInfeccao() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [unidade, setUnidade] = useState('Todas');
  const [germeSel, setGermeSel] = useState(null);

  const carregar = async () => {
    setCarregando(true); setErro('');
    try { setDados(await buscarIW('iw-pacientes')); } catch (e) { setErro(e.message); }
    setCarregando(false);
  };
  useEffect(() => { carregar(); }, []);

  const pacientes = useMemo(() => (dados?.pacientes || []).filter(p => unidade === 'Todas' || p.unidade === unidade), [dados, unidade]);
  const contagemUnidade = useMemo(() => {
    const c = { Todas: (dados?.pacientes || []).length, VitalCare: 0, HomeBaby: 0 };
    (dados?.pacientes || []).forEach(p => { if (c[p.unidade] !== undefined) c[p.unidade]++; });
    return c;
  }, [dados]);

  const resumo = useMemo(() => {
    const total = pacientes.length;
    const colonizados = pacientes.filter(p => GERMES.some(g => p.infeccao.germes[g.id] === 'sim'));
    const contato = pacientes.filter(p => p.infeccao.precaucoes.contato === 'sim').length;
    const desconhecido = pacientes.filter(p => /desconhec/i.test(p.infeccao.colonizacao)).length;
    const camposGerme = total * GERMES.length;
    const emBranco = pacientes.reduce((s, p) => s + GERMES.filter(g => p.infeccao.germes[g.id] === 'semInfo').length, 0);
    const germes = GERMES.map(g => ({ ...g, qtd: pacientes.filter(p => p.infeccao.germes[g.id] === 'sim').length }));
    const precaucoes = PRECAUCOES.map(pr => {
      const c = { sim: 0, nao: 0, semInfo: 0 };
      pacientes.forEach(p => { c[p.infeccao.precaucoes[pr.id]]++; });
      return { ...pr, ...c };
    });
    return { total, colonizados: colonizados.length, contato, desconhecido, pctBranco: camposGerme ? Math.round(emBranco / camposGerme * 100) : 0, germes, precaucoes };
  }, [pacientes]);

  const lista = useMemo(() => pacientes
    .filter(p => GERMES.some(g => p.infeccao.germes[g.id] === 'sim') || p.infeccao.precaucoes.contato === 'sim')
    .filter(p => !germeSel || p.infeccao.germes[germeSel] === 'sim')
    .sort((a, b) => a.nome.localeCompare(b.nome)), [pacientes, germeSel]);

  const maxGerme = Math.max(1, ...resumo.germes.map(g => g.qtd));

  return (
    <div style={{ padding: '24px 28px', background: P.bg, minHeight: '100%', color: P.text, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: P.primaryDark }}>Dashboard</div>
          <h1 style={{ margin: '4px 0', fontSize: '1.6rem', fontWeight: 800 }}>Controle de Infecção</h1>
          <div style={{ fontSize: '0.86rem', color: P.muted }}>Colonização e precauções · dados do IW · exportação de {fmtExportacao(dados?.atualizadoEm)} · {pacientes.length} pacientes em atendimento{unidade !== 'Todas' ? ` na ${unidade}` : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4, background: '#e2e8f0', padding: 4, borderRadius: 10 }}>
            {UNIDADES.map(u => (
              <button key={u} onClick={() => setUnidade(u)}
                style={{ border: 0, borderRadius: 8, padding: '7px 13px', fontSize: '0.8rem', fontWeight: unidade === u ? 800 : 600, cursor: 'pointer', background: unidade === u ? P.white : 'transparent', color: P.text }}>
                {u} · {contagemUnidade[u] ?? 0}
              </button>
            ))}
          </div>
          <button onClick={carregar} title="Recarregar" aria-label="Recarregar" style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 10, padding: 9, cursor: 'pointer', display: 'flex' }}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {erro && <div style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 10, padding: '10px 14px', fontSize: '0.86rem' }}>Não foi possível carregar: {erro}</div>}
      {carregando && <div style={{ color: P.muted }}>Carregando dados do IW…</div>}

      {!carregando && !erro && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
            <Kpi titulo="Pacientes colonizados" valor={resumo.colonizados} detalhe={`${resumo.total ? Math.round(resumo.colonizados / resumo.total * 100) : 0}% · com pelo menos um germe marcado "Sim"`} />
            <Kpi titulo="Em precaução de contato" valor={resumo.contato} detalhe={`${resumo.total ? Math.round(resumo.contato / resumo.total * 100) : 0}% dos pacientes em atendimento`} />
            <Kpi titulo='Rastreio "desconhecido"' valor={resumo.desconhecido} detalhe="colonização registrada como desconhecida no IW" />
            <Kpi titulo="Sem preenchimento no IW" valor={`${resumo.pctBranco}%`} detalhe="dos campos de germe em branco" alerta />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
            <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 16, padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontWeight: 800 }}>Colonização por germe <span style={{ fontWeight: 500, fontSize: '0.78rem', color: P.muted }}>· clique para filtrar a lista</span></div>
              {resumo.germes.map(g => (
                <button key={g.id} onClick={() => setGermeSel(germeSel === g.id ? null : g.id)}
                  style={{ display: 'grid', gridTemplateColumns: '130px minmax(0,1fr) 36px', gap: 12, alignItems: 'center', fontSize: '0.86rem', border: 0, borderRadius: 8, padding: '4px 6px', cursor: 'pointer', textAlign: 'left',
                    background: germeSel === g.id ? '#fff7ed' : 'transparent' }}>
                  <span style={{ fontWeight: 600, color: '#334155' }}>{g.nome}</span>
                  <span style={{ height: 12, borderRadius: 6, background: '#f1f5f9', overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: 12, borderRadius: 6, background: '#c2410c', width: `${Math.round(g.qtd / maxGerme * 100)}%` }} />
                  </span>
                  <span style={{ fontWeight: 800, textAlign: 'right' }}>{fmtPequeno(g.qtd)}</span>
                </button>
              ))}
              <div style={{ fontSize: '0.74rem', color: '#64748b' }}>Menos de 5 casos aparecem como "&lt;5" para não expor pacientes em números pequenos.</div>
            </div>
            <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 16, padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontWeight: 800 }}>Precauções</div>
              {resumo.precaucoes.map(p => {
                const t = Math.max(1, p.sim + p.nao + p.semInfo);
                return (
                  <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.86rem' }}>
                      <span style={{ fontWeight: 600, color: '#334155' }}>{p.nome}</span>
                      <span style={{ color: P.muted }}>{fmtPequeno(p.sim)} sim · {p.nao} não · {p.semInfo} sem info</span>
                    </div>
                    <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', background: '#f1f5f9' }}>
                      <span style={{ background: '#1d4ed8', width: `${p.sim / t * 100}%` }} />
                      <span style={{ background: '#93c5fd', width: `${p.nao / t * 100}%` }} />
                      <span style={{ background: '#fdba74', width: `${p.semInfo / t * 100}%` }} />
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: '0.74rem', color: P.muted }}>
                {[['#1d4ed8', 'Sim'], ['#93c5fd', 'Não'], ['#fdba74', 'Desconhecido / em branco']].map(([c, l]) => (
                  <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: c }} />{l}</span>
                ))}
              </div>
            </div>
          </div>

          <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${P.border}`, fontWeight: 800 }}>
              Pacientes colonizados ou em precaução de contato · {lista.length}
              {germeSel && <span style={{ fontWeight: 600, color: P.muted }}> · filtrando {GERMES.find(g => g.id === germeSel)?.nome} <button onClick={() => setGermeSel(null)} style={{ border: 0, background: 'none', color: '#4338ca', cursor: 'pointer', fontWeight: 700 }}>limpar</button></span>}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', minWidth: 820 }}>
                <thead>
                  <tr style={{ background: P.bg, textAlign: 'left', color: P.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                    {['Paciente', 'Nº IW', 'Unidade', 'Germes', 'Precaução', 'Colonização (texto do IW)'].map(h => <th key={h} style={{ padding: '11px 14px', fontWeight: 700 }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {lista.map(p => {
                    const g = GERMES.filter(x => p.infeccao.germes[x.id] === 'sim');
                    const pr = PRECAUCOES.filter(x => p.infeccao.precaucoes[x.id] === 'sim').map(x => x.nome);
                    return (
                      <tr key={p.numIW} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '11px 14px', fontWeight: 700 }}>{p.nome}</td>
                        <td style={{ padding: '11px 14px', color: P.muted }}>{p.numIW}</td>
                        <td style={{ padding: '11px 14px' }}>{p.unidade}</td>
                        <td style={{ padding: '11px 14px' }}>
                          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {g.length === 0 ? <span style={{ fontSize: '0.74rem', color: '#64748b' }}>Sem germe marcado</span>
                              : g.map(x => <span key={x.id} style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#ffedd5', color: '#9a3412' }}>{x.nome}</span>)}
                          </span>
                        </td>
                        <td style={{ padding: '11px 14px' }}>{pr.join(', ') || '—'}{p.infeccao.precaucoes.motivo && <div style={{ fontSize: '0.72rem', color: P.muted }}>motivo: {p.infeccao.precaucoes.motivo}</div>}</td>
                        <td style={{ padding: '11px 14px', color: P.muted }}>{p.infeccao.colonizacao || '—'}</td>
                      </tr>
                    );
                  })}
                  {lista.length === 0 && <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Nenhum paciente com esse filtro.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 14, padding: 14, fontSize: '0.86rem', color: '#312e81' }}>
            <strong>Sugestão para a equipe:</strong> padronizar no IW o campo de colonização (hoje é texto livre: "Desconhecida", "ESBL 2022", "Negativo 16/07/2026"...).
            Com 3 opções fixas (Positivo / Negativo / Desconhecido) e a data do rastreio, o painel passa a mostrar a evolução mês a mês.
          </div>
        </>
      )}
    </div>
  );
}
